import { useState } from 'react';
import { X, Save, Eye, EyeOff, Palette, Type, Maximize2, Square, Box, Layout } from 'lucide-react';
import { ThemePreview } from './ThemePreview';

interface Theme {
  id: string;
  label: string;
  description: string;
  preview: {
    primary: string;
    secondary: string;
  };
  tokens: {
    colors: Record<string, string>;
    typography: Record<string, string>;
    spacing: Record<string, string>;
    borders: Record<string, string>;
    shadows: Record<string, string>;
    layout: Record<string, string>;
  };
}

interface ThemeFormDialogProps {
  theme?: Theme;
  existingThemeIds: string[];
  onSave: (theme: Theme) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

// Default token structure for new themes
const DEFAULT_TOKENS = {
  colors: {
    primary: '#1e3a5f',
    secondary: '#d4a574',
    bg: '#ffffff',
    'bg-alt': '#f8f7f4',
    text: '#1a1a1a',
    'text-secondary': '#6b7280',
    border: '#e5e5e5',
    link: '#1e3a5f',
  },
  typography: {
    'font-heading': 'Playfair Display',
    'font-body': 'Inter',
    'size-hero': '3.75rem',
    'size-base': '1rem',
  },
  spacing: {
    section: '4rem',
  },
  borders: {
    'radius-md': '0.5rem',
  },
  shadows: {
    md: '0 4px 6px rgba(0,0,0,0.07)',
  },
  layout: {
    'max-width': '1280px',
    'max-width-narrow': '768px',
    gutter: '1.5rem',
  },
};

export function ThemeFormDialog({
  theme,
  existingThemeIds,
  onSave,
  onCancel,
  isSaving,
}: ThemeFormDialogProps) {
  const isEditing = !!theme;

  // Form state
  const [id, setId] = useState(theme?.id || '');
  const [label, setLabel] = useState(theme?.label || '');
  const [description, setDescription] = useState(theme?.description || '');
  const [previewPrimary, setPreviewPrimary] = useState(theme?.preview.primary || '#1e3a5f');
  const [previewSecondary, setPreviewSecondary] = useState(theme?.preview.secondary || '#d4a574');
  const [tokens, setTokens] = useState(theme?.tokens || DEFAULT_TOKENS);
  const [showPreview, setShowPreview] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // ID validation
    if (!id.trim()) {
      newErrors.id = 'ID richiesto';
    } else if (!/^[a-z0-9-]+$/.test(id)) {
      newErrors.id = 'ID deve contenere solo lettere minuscole, numeri e trattini';
    } else if (!isEditing && existingThemeIds.includes(id)) {
      newErrors.id = 'ID già esistente';
    }

    // Label validation
    if (!label.trim()) {
      newErrors.label = 'Label richiesto';
    }

    // Description validation
    if (!description.trim()) {
      newErrors.description = 'Descrizione richiesta';
    }

    // Preview colors validation
    if (!/^#[0-9A-Fa-f]{6}$/.test(previewPrimary)) {
      newErrors.previewPrimary = 'Colore primario non valido (formato: #RRGGBB)';
    }
    if (!/^#[0-9A-Fa-f]{6}$/.test(previewSecondary)) {
      newErrors.previewSecondary = 'Colore secondario non valido (formato: #RRGGBB)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const themeData: Theme = {
      id: id.trim(),
      label: label.trim(),
      description: description.trim(),
      preview: {
        primary: previewPrimary,
        secondary: previewSecondary,
      },
      tokens,
    };

    try {
      await onSave(themeData);
    } catch (error) {
      console.error('Save error:', error);
      setErrors({ submit: 'Errore durante il salvataggio' });
    }
  };

  const handleTokenChange = (category: string, key: string, value: string) => {
    setTokens((prev) => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [key]: value,
      },
    }));
  };

  // Build preview data
  const previewData: Theme = {
    id,
    label: label || 'Nuovo Tema',
    description,
    preview: {
      primary: previewPrimary,
      secondary: previewSecondary,
    },
    tokens,
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Modifica Tema' : 'Nuovo Tema'}
            </h2>
            {isEditing && (
              <p className="text-sm text-gray-500 mt-1">Tema ID: {theme.id}</p>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="btn-secondary flex items-center space-x-2"
            >
              {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span>{showPreview ? 'Nascondi' : 'Anteprima'}</span>
            </button>
            <button
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className={`grid ${showPreview ? 'grid-cols-2' : 'grid-cols-1'} gap-6 p-6`}>
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Metadata Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Metadati
                </h3>

                {/* Theme ID */}
                <div>
                  <label htmlFor="theme-id" className="block text-sm font-medium text-gray-700 mb-1">
                    Theme ID *
                  </label>
                  <input
                    id="theme-id"
                    type="text"
                    value={id}
                    onChange={(e) => setId(e.target.value.toLowerCase())}
                    disabled={isEditing}
                    className={`input w-full ${errors.id ? 'border-red-500' : ''}`}
                    placeholder="institutional"
                  />
                  {errors.id && <p className="text-xs text-red-600 mt-1">{errors.id}</p>}
                </div>

                {/* Label */}
                <div>
                  <label htmlFor="theme-label" className="block text-sm font-medium text-gray-700 mb-1">
                    Label *
                  </label>
                  <input
                    id="theme-label"
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className={`input w-full ${errors.label ? 'border-red-500' : ''}`}
                    placeholder="Istituzionale"
                  />
                  {errors.label && <p className="text-xs text-red-600 mt-1">{errors.label}</p>}
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="theme-desc" className="block text-sm font-medium text-gray-700 mb-1">
                    Descrizione *
                  </label>
                  <textarea
                    id="theme-desc"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className={`input w-full ${errors.description ? 'border-red-500' : ''}`}
                    rows={2}
                    placeholder="Professionale e autorevole..."
                  />
                  {errors.description && <p className="text-xs text-red-600 mt-1">{errors.description}</p>}
                </div>

                {/* Preview Colors */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="preview-primary" className="block text-sm font-medium text-gray-700 mb-1">
                      Preview Primary *
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={previewPrimary}
                        onChange={(e) => setPreviewPrimary(e.target.value)}
                        className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        id="preview-primary"
                        type="text"
                        value={previewPrimary}
                        onChange={(e) => setPreviewPrimary(e.target.value)}
                        className={`input flex-1 ${errors.previewPrimary ? 'border-red-500' : ''}`}
                        placeholder="#1e3a5f"
                      />
                    </div>
                    {errors.previewPrimary && <p className="text-xs text-red-600 mt-1">{errors.previewPrimary}</p>}
                  </div>
                  <div>
                    <label htmlFor="preview-secondary" className="block text-sm font-medium text-gray-700 mb-1">
                      Preview Secondary *
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={previewSecondary}
                        onChange={(e) => setPreviewSecondary(e.target.value)}
                        className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        id="preview-secondary"
                        type="text"
                        value={previewSecondary}
                        onChange={(e) => setPreviewSecondary(e.target.value)}
                        className={`input flex-1 ${errors.previewSecondary ? 'border-red-500' : ''}`}
                        placeholder="#d4a574"
                      />
                    </div>
                    {errors.previewSecondary && <p className="text-xs text-red-600 mt-1">{errors.previewSecondary}</p>}
                  </div>
                </div>
              </div>

              {/* Token Categories */}
              {/* Colors */}
              <TokenCategoryEditor
                title="Colors"
                icon={Palette}
                tokens={tokens.colors}
                onChange={(key, value) => handleTokenChange('colors', key, value)}
                colorInputs
              />

              {/* Typography */}
              <TokenCategoryEditor
                title="Typography"
                icon={Type}
                tokens={tokens.typography}
                onChange={(key, value) => handleTokenChange('typography', key, value)}
              />

              {/* Spacing */}
              <TokenCategoryEditor
                title="Spacing"
                icon={Maximize2}
                tokens={tokens.spacing}
                onChange={(key, value) => handleTokenChange('spacing', key, value)}
              />

              {/* Borders */}
              <TokenCategoryEditor
                title="Borders"
                icon={Square}
                tokens={tokens.borders}
                onChange={(key, value) => handleTokenChange('borders', key, value)}
              />

              {/* Shadows */}
              <TokenCategoryEditor
                title="Shadows"
                icon={Box}
                tokens={tokens.shadows}
                onChange={(key, value) => handleTokenChange('shadows', key, value)}
              />

              {/* Layout */}
              <TokenCategoryEditor
                title="Layout"
                icon={Layout}
                tokens={tokens.layout}
                onChange={(key, value) => handleTokenChange('layout', key, value)}
              />

              {/* Submit Error */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{errors.submit}</p>
                </div>
              )}
            </form>

            {/* Preview */}
            {showPreview && (
              <div className="border-l border-gray-200 pl-6">
                <ThemePreview theme={previewData} />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="btn-secondary"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="btn-primary flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>{isSaving ? 'Salvataggio...' : 'Salva Tema'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper component for token category editors
interface TokenCategoryEditorProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  tokens: Record<string, string>;
  onChange: (key: string, value: string) => void;
  colorInputs?: boolean;
}

function TokenCategoryEditor({ title, icon: Icon, tokens, onChange, colorInputs }: TokenCategoryEditorProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2 border-b border-gray-200 pb-2">
        <Icon className="w-5 h-5 text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <span className="text-sm text-gray-500">({Object.keys(tokens).length})</span>
      </div>
      <div className="grid grid-cols-1 gap-3">
        {Object.entries(tokens).map(([key, value]) => (
          <div key={key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {key}
            </label>
            {colorInputs && key !== 'link' ? (
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={value}
                  onChange={(e) => onChange(key, e.target.value)}
                  className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={value}
                  onChange={(e) => onChange(key, e.target.value)}
                  className="input flex-1 font-mono text-sm"
                />
              </div>
            ) : (
              <input
                type="text"
                value={value}
                onChange={(e) => onChange(key, e.target.value)}
                className="input w-full font-mono text-sm"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
